import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Code, Flame, Target, Trophy, ArrowRight, Zap, CheckCircle2 } from "lucide-react";

const ProblemsSection = () => {
  return (
    <section className="theme-light bg-background relative py-32 overflow-hidden">
      {/* Background Ambience */}
      <div className="absolute inset-0 pointer-events-none z-0">
        <div className="hero-bg-dot-grid" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary rounded-full blur-[150px] opacity-[0.03]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          className="text-center mb-20 max-w-3xl mx-auto"
        >
          <div className="border-primary-subtle bg-primary-subtle inline-flex items-center gap-2 px-3 py-1.5 border mb-8">
            <Code className="w-3.5 h-3.5 text-primary" />
            <span className="text-[9px] font-semibold text-primary uppercase tracking-[0.3em]">Coding Challenges</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-semibold text-foreground leading-[1.05] mb-6 uppercase tracking-tighter">
            Master Algorithms. <br />
            <span className="text-primary italic">Build Consistency.</span>
          </h2>
          <p className="font-sora text-muted-foreground text-lg leading-relaxed">
            Solve <span className="text-foreground font-bold">200 curated</span> technical interview problems and build an unbreakable streak with our dynamic Daily Challenge system.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Card 1: 200 Problems Catalog */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <Card className="bg-card border-border p-10 h-full flex flex-col justify-between group overflow-hidden relative atm-card">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2 pointer-events-none" />
              
              <div>
                <div className="bg-background border-border w-12 h-12 border rounded-lg flex items-center justify-center mb-6 relative z-10">
                  <Code className="w-6 h-6 text-primary" />
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-primary" />
                </div>
                
                <h3 className="text-[13px] font-bold text-foreground mb-3 uppercase tracking-wide relative z-10">The Core 200</h3>
                <p className="font-sora text-muted-foreground text-sm leading-relaxed mb-8 relative z-10">
                  Stop wasting time on thousands of random problems. We've distilled the technical interview down to the absolute most critical 200 algorithms spanning arrays, DP, graphs, and system design.
                </p>

                <div className="space-y-4 mb-10 relative z-10">
                  {['Curated by MAANG Engineers', 'In-Browser Execution', 'Detailed Edge Cases & Hints'].map((feature, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                      <span className="font-sora text-sm text-muted-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative z-10 mt-auto">
                <Link to="/problems">
                  <Button variant="outline" className="w-full h-14 border-primary text-primary hover:bg-primary hover:text-background font-bold text-xs uppercase tracking-widest rounded-none">
                    Browse Catalog <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </Card>
          </motion.div>

          {/* Card 2: Daily Challenge */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <Card className="bg-card border-border p-10 h-full flex flex-col justify-between group overflow-hidden relative atm-card">
              <div className="absolute top-0 right-0 w-64 h-64 bg-accent/10 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2 pointer-events-none" />
              
              <div>
                <div className="bg-background border-border w-12 h-12 border rounded-lg flex items-center justify-center mb-6 relative z-10">
                  <Flame className="w-6 h-6 text-accent" />
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-accent animate-pulse" />
                </div>
                
                <h3 className="text-[13px] font-bold text-foreground mb-3 uppercase tracking-wide relative z-10">Daily Consistency</h3>
                <p className="font-sora text-muted-foreground text-sm leading-relaxed mb-8 relative z-10">
                  Discipline beats motivation. Lock in your habits with our rotating daily challenge. Track your streak, compete globally, and watch your problem-solving intuition skyrocket.
                </p>

                <div className="space-y-4 mb-10 relative z-10">
                  {['New Problem Every 24 Hours', 'Global Streak Tracking', 'Focus Mode Workspace'].map((feature, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Zap className="w-4 h-4 text-accent shrink-0" />
                      <span className="font-sora text-sm text-muted-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative z-10 mt-auto">
                <Link to="/problems/daily-challenge">
                  <Button className="w-full h-14 bg-primary hover:opacity-90 text-background font-semibold text-xs uppercase tracking-widest rounded-none shadow-[0_0_20px_rgba(5,150,105,0.2)]">
                    Solve Today's Challenge <Flame className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </Card>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default ProblemsSection;
